import React, { useState, useEffect, useCallback, useRef } from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { makeStyles } from '@mui/styles';
import axios from "../utils/axiosConfig";
import { fetchMe } from "../utils/authService";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import { WEB_URL, PRIVACY_POLICY_WEB_URL } from "../utils/constants";
import LoaderProcessing from "../components/LoaderProcessing";
import OtpInput from "../components/OtpInput";
import ahGreenDotLogo from "../images/assesshub_logo_green_dot.svg";
import SupportBot from "./SupportBot";

function Copyright() {
    return (
        <Typography variant="body2" color="textSecondary" align="center">
            {"By continuing, you agree to AssessHub's "}
            <Link color="inherit" href={PRIVACY_POLICY_WEB_URL} target="_blank">
                Privacy Policy
            </Link>{" "}
            {"."}
            <br /><br />
            {"Copyright © "}
            <Link color="inherit" href={WEB_URL}>
                AssessHub
            </Link>{" "}
            {new Date().getFullYear()}
            {"."}
        </Typography>
    );
}

const useStyles = makeStyles((theme) => ({
    root: {
        height: "100vh",
    },
    image: {
        backgroundImage: "url('./../../v2/images/login-page.svg')",
        backgroundRepeat: "no-repeat",
        backgroundColor: theme?.palette?.mode === "dark" ? theme?.palette?.grey?.[900] : theme?.palette?.grey?.[50] || '#fafafa',
        backgroundSize: "cover",
        backgroundPosition: "center",
    },
    paper: {
        padding: theme?.spacing?.(8, 4) || '64px 32px',
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100%",
        boxSizing: "border-box",
        '@media (max-width: 600px)': {
            padding: '40px 20px',
        },
        '@media (max-width: 400px)': {
            padding: '24px 16px',
        },
    },
    form: {
        width: "100%",
        maxWidth: "400px",
        marginTop: theme?.spacing?.(3) || '24px',
        padding: '0 8px',
        boxSizing: 'border-box',
    },
    submit: {
        margin: theme?.spacing?.(3, 0, 2) || '24px 0 16px',
        minHeight: '48px',
        fontSize: '1rem',
    },
    logo: {
        maxWidth: "100%",
        width: "450px",
        maxHeight: "70px",
        objectFit: "contain",
        '@media (max-width: 400px)': {
            width: '280px',
            maxHeight: '50px',
        },
    },
    otpContainer: {
        textAlign: 'center',
        marginTop: '20px',
    },
    emailInfo: {
        color: '#666',
        marginBottom: '10px',
        fontSize: '14px',
    },
    resendContainer: {
        marginTop: '16px',
        textAlign: 'center',
    },
    resendButton: {
        color: '#6f2981',
        cursor: 'pointer',
        textDecoration: 'underline',
        border: 'none',
        background: 'none',
        fontSize: '14px',
        '&:disabled': {
            color: '#999',
            cursor: 'not-allowed',
            textDecoration: 'none',
        },
    },
    backButton: {
        marginTop: '16px',
        color: '#666',
        cursor: 'pointer',
        fontSize: '14px',
        textDecoration: 'underline',
        border: 'none',
        background: 'none',
    },
    countdown: {
        color: '#999',
        fontSize: '14px',
    },
}));

export default function Signin2FA(props) {
    const classes = useStyles();

    // Stage: 'credentials' or 'otp'
    const [stage, setStage] = useState('credentials');

    // Credentials state
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    // OTP state
    const [otp, setOtp] = useState("");
    const [userId, setUserId] = useState(null);
    const [maskedEmail, setMaskedEmail] = useState("");
    const [otpInfoMessage, setOtpInfoMessage] = useState(""); // Message about OTP (new or existing)

    // UI state
    const [errorMsg, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [loginCheckModal, setLoginCheckModal] = useState(false);
    const [showModel, setModelShow] = useState(false);

    // Resend cooldown
    const [cooldown, setCooldown] = useState(0);

    // Synchronous guard against double-click/double-tap firing two resend
    // requests before the `loading` state re-render disables the button.
    const resendInFlightRef = useRef(false);

    // Countdown timer effect
    useEffect(() => {
        let timer;
        if (cooldown > 0) {
            timer = setInterval(() => {
                setCooldown(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [cooldown]);

    const handleClose = () => {
        setModelShow(false);
        setLoginCheckModal(false);
    };

    const handleSession = () => {
        setError(props.forceLogout);
    };

    const handleReLogin = (event) => {
        setLoginCheckModal(false);
        handleCredentialsSubmit(event, true);
    };

    // Handle successful login — backend has set HttpOnly auth_token cookie;
    // we hydrate Redux from /me and signal the app.
    const completeLogin = useCallback(async () => {
        await fetchMe();
        props.setUser(true);
    }, [props]);

    // Handle credentials submission
    const handleCredentialsSubmit = async (event, regen = false) => {
        event.preventDefault();
        setLoading(true);
        setError("");

        sessionStorage.setItem("loginEmail", email.toLowerCase());

        try {
            const response = await axios.post("/login-2fa/initiate", {
                userName: email.toLowerCase(),
                password,
                regen: regen,
            });

            setLoading(false);
            const data = response.data;

            // Handle failed login attempts
            if (data.remainingAttempts !== undefined) {
                if (data.remainingAttempts > 1) {
                    setError(<span>Username or Password is incorrect.</span>);
                } else if (data.remainingAttempts > 0) {
                    setError(
                        <span>
                            One last attempt remaining !!<br />
                            WARNING: Too many unsuccessful attempts can cause the account to lock out!
                        </span>
                    );
                } else {
                    setError(
                        <span>This account is locked due to too many unsuccessful attempts. Please try again after {data.timeRemaining} {data.timeRemaining !== 1 ? "minutes" : "minute"}.</span>
                    );
                }
                return;
            }

            // Handle blocked account
            if (data.status === "BLOCKED") {
                setError(<span>Account is temporarily locked. Please try again later.</span>);
                return;
            }

            // Handle rate limiting
            if (data.status === "RATE_LIMITED") {
                setError(<span>{data.message}</span>);
                return;
            }

            // Handle account with no 2FA-enabled org — /signin is 2FA-only.
            if (data.status === "NOT_2FA_ACCOUNT") {
                setError(<span>{data.message || "2FA is not enabled for your account. Contact System Administrator."}</span>);
                return;
            }

            // Handle OTP required (2FA enabled)
            if (data.status === "OTP_REQUIRED") {
                setUserId(data.userId);
                setMaskedEmail(data.email);
                setCooldown(data.cooldownSeconds || 0);
                setOtpInfoMessage(data.message || "Verification code sent to your email");
                setStage('otp');
                setOtp("");
                return;
            }

            // Handle successful login (no 2FA)
            if (data.status === "SUCCESS" || data.status === "Token Regenerate complete successfully") {
                await completeLogin();
                return;
            }

            // Handle already logged in scenarios
            if (data.status === "User Already logged in, logout to proceed further or contact support") {
                setModelShow(true);
                return;
            }

            if (data.message === "you are already logged in") {
                setLoginCheckModal(true);
                return;
            }

        } catch (error) {
            setLoading(false);
            // Handle rate limiting (429) responses
            if (error.response?.status === 429) {
                const data = error.response.data;
                setError(<span>{data.message || "Too many requests. Please wait and try again."}</span>);
                if (data.waitSeconds) {
                    setCooldown(data.waitSeconds);
                }
                return;
            }
            setError(error.response?.data?.error || error.response?.data?.message || "An error occurred. Please try again.");
        }
    };

    // Handle OTP verification
    const handleOTPSubmit = async (event, otpValue = null) => {
        if (event) event.preventDefault();
        setLoading(true);
        setError("");

        // Use passed otpValue or fall back to state
        const otpToVerify = otpValue || otp;

        try {
            const response = await axios.post("/login-2fa/verify", {
                userId,
                otp: otpToVerify,
                userName: email.toLowerCase(),
                regen: false,
            });

            setLoading(false);
            const data = response.data;

            if (data.status === "INVALID_OTP") {
                setError(<span>{data.message}</span>);
                setOtp("");
                return;
            }

            if (data.status === "EXPIRED_OTP") {
                setError(<span>{data.message}</span>);
                setOtp("");
                return;
            }

            if (data.status === "SUCCESS" || data.status === "Token Regenerate complete successfully") {
                await completeLogin();
                return;
            }

        } catch (error) {
            setLoading(false);
            setError(error.response?.data?.error || error.response?.data?.message || "Verification failed. Please try again.");
        }
    };

    // Handle OTP resend
    const handleResendOTP = async () => {
        if (cooldown > 0) return;
        if (resendInFlightRef.current) return;
        resendInFlightRef.current = true;

        setLoading(true);
        setError("");

        try {
            const response = await axios.post("/login-2fa/resend", {
                userId,
                userName: email.toLowerCase(),
            });

            setLoading(false);
            const data = response.data;

            if (data.status === "COOLDOWN" || data.status === "RATE_LIMITED") {
                setError(<span>{data.message}</span>);
                setCooldown(data.waitSeconds || 60);
                return;
            }

            if (data.status === "OTP_SENT") {
                setCooldown(data.cooldownSeconds || 60);
                setError(""); // Clear any previous errors
                setOtpInfoMessage("New verification code sent to your email");
                // Clear any stale digits so a leftover old code can't be
                // submitted after this resend has invalidated it.
                setOtp("");
            }

        } catch (error) {
            setLoading(false);
            // Handle rate limiting (429) responses
            if (error.response?.status === 429) {
                const data = error.response.data;
                setError(<span>{data.message || "Too many requests. Please wait and try again."}</span>);
                if (data.waitSeconds) {
                    setCooldown(data.waitSeconds);
                }
                return;
            }
            setError(error.response?.data?.error || error.response?.data?.message || "Failed to resend code. Please try again.");
        } finally {
            resendInFlightRef.current = false;
        }
    };

    // Go back to credentials stage
    const handleBackToCredentials = () => {
        setStage('credentials');
        setOtp("");
        setUserId(null);
        setMaskedEmail("");
        setOtpInfoMessage("");
        setError("");
        setCooldown(0);
    };

    // Handle OTP completion (auto-submit when 6 digits entered)
    const handleOTPComplete = (completedOtp) => {
        setOtp(completedOtp);
        // Auto-submit after a brief delay to show the completed OTP
        setTimeout(() => {
            if (completedOtp.length === 6) {
                handleOTPSubmit(null, completedOtp);
            }
        }, 300);
    };

    const UserReLoginModal = () => (
        <Dialog
            PaperProps={{
                style: { borderRadius: "10px", textAlign: "center", maxWidth: "380px", width: "calc(100% - 32px)", margin: "16px" },
            }}
            open={loginCheckModal}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <div style={{ textAlign: "center" }}>
                <h3 id="transition-modal-title" style={{ color: "#6f2981", marginBottom: "30px" }}>
                    System Warning
                </h3>
            </div>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    <span>You are already logged in on another device. Would you like to login to this device and logout from the other device?</span>
                </DialogContentText>

                <div className="form-group">
                    <ul className="list-inline btn-list">
                        <li>
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                            <a href="#" className="btn btn-blue" onClick={handleReLogin}>
                                Yes
                            </a>
                        </li>
                        <li>
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                            <a href="#" className="btn btn-blue" onClick={handleClose} autoFocus>
                                No
                            </a>
                        </li>
                    </ul>
                </div>
            </DialogContent>
        </Dialog>
    );

    const SessionActiveModal = () => (
        <Dialog
            PaperProps={{
                style: { borderRadius: "10px", textAlign: "center", maxWidth: "380px", width: "calc(100% - 32px)", margin: "16px" },
            }}
            open={showModel}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <div style={{ textAlign: "center" }}>
                <h3 id="transition-modal-title" style={{ color: "#6f2981", marginBottom: "30px" }}>
                    System Warning
                </h3>
            </div>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    <span>The session is active. Do you want to re-login to the system?</span>
                </DialogContentText>

                <div className="form-group">
                    <ul className="list-inline btn-list">
                        <li>
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                            <a href="#" className="btn btn-blue" onClick={handleReLogin}>
                                Yes
                            </a>
                        </li>
                        <li>
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                            <a href="#" className="btn btn-blue" onClick={handleClose} autoFocus>
                                No
                            </a>
                        </li>
                    </ul>
                </div>
            </DialogContent>
        </Dialog>
    );

    return (
        <>
            <UserReLoginModal />
            <SessionActiveModal />
            <Grid container component="main" id="main-body-two" className={classes.root} onLoad={handleSession}>
                <CssBaseline />
                <Grid item xs={false} sm={4} md={7} className={classes.image} />
                <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
                    <div className={classes.paper}>
                        <img className={`img-responsive center-block ${classes.logo}`} src={ahGreenDotLogo} alt="AssessHub Logo" />
                        <Box
                            sx={{
                                maxWidth: "400px",
                                width: "100%",
                                mt: 2,
                                mb: 1,
                                p: 1.5,
                                backgroundColor: "#f5f0fa",
                                borderLeft: "3px solid #7b60b0",
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1,
                            }}
                        >
                            <Typography variant="caption" sx={{ color: "#555", lineHeight: 1.4 }}>
                                This platform uses strictly necessary cookies for secure login and assessment delivery. No tracking or marketing cookies are used.
                            </Typography>
                        </Box>
                        {stage === 'credentials' ? (
                            // Stage 1: Credentials Form
                            <form className={classes.form} onSubmit={(e) => handleCredentialsSubmit(e, false)} noValidate>
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="email"
                                    label="Username"
                                    name="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value.toLowerCase())}
                                />

                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    name="password"
                                    label="Password"
                                    type="password"
                                    id="password"
                                    autoComplete="current-password"
                                    onChange={(event) => setPassword(event.target.value)}
                                />

                                <Link href='/v2/user/forgot-password' style={{ textDecoration: "none" }}>
                                    <div style={{ width: "100%", textAlign: "right", marginBottom: "10px", color: "rgb(123,96,176)", cursor: "pointer" }}>
                                        Forgot Password?
                                    </div>
                                </Link>

                                {!!errorMsg && <Typography style={{ color: "red" }}>{errorMsg}</Typography>}

                                {/* Show countdown timer when rate limited */}
                                {cooldown > 0 && (
                                    <Typography style={{ color: "#666", textAlign: "center", marginTop: "8px" }}>
                                        Please wait {cooldown}s before trying again
                                    </Typography>
                                )}

                                {loading ? (
                                    <div className="loaderProcess" style={{ margin: "24px 0px 16px" }}>
                                        <LoaderProcessing />
                                    </div>
                                ) : (
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        className={classes.submit}
                                        disabled={cooldown > 0}
                                    >
                                        {cooldown > 0 ? `Wait ${cooldown}s` : 'Continue'}
                                    </Button>
                                )}
                                <br /><br /><br /><br />
                                <Box mt={5}>
                                    <Copyright />
                                </Box>
                            </form>
                        ) : (
                            // Stage 2: OTP Verification
                            <div className={classes.form}>
                                <div className={classes.otpContainer}>
                                    <Typography variant="h6" style={{ marginBottom: '16px', color: '#333' }}>
                                        Enter Verification Code
                                    </Typography>

                                    <Typography className={classes.emailInfo} style={{ marginBottom: '8px' }}>
                                        {otpInfoMessage}
                                    </Typography>
                                    <Typography className={classes.emailInfo} style={{ fontSize: '13px', color: '#888' }}>
                                        ({maskedEmail})
                                    </Typography>

                                    <OtpInput
                                        length={6}
                                        value={otp}
                                        onChange={setOtp}
                                        onComplete={handleOTPComplete}
                                        error={!!errorMsg}
                                        disabled={loading}
                                    />

                                    <Typography style={{ color: "red", marginTop: '8px' }}>{errorMsg}</Typography>

                                    {loading ? (
                                        <div className="loaderProcess" style={{ margin: "24px 0px 16px" }}>
                                            <LoaderProcessing />
                                        </div>
                                    ) : (
                                        <Button
                                            type="button"
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            className={classes.submit}
                                            onClick={handleOTPSubmit}
                                            disabled={otp.length !== 6}
                                        >
                                            Verify & Sign In
                                        </Button>
                                    )}

                                    <div className={classes.resendContainer}>
                                        {cooldown > 0 ? (
                                            <span className={classes.countdown}>
                                                Resend code in {cooldown}s
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                className={classes.resendButton}
                                                onClick={handleResendOTP}
                                                disabled={loading}
                                            >
                                                Resend Code
                                            </button>
                                        )}
                                    </div>

                                    <button
                                        type="button"
                                        className={classes.backButton}
                                        onClick={handleBackToCredentials}
                                        disabled={loading}
                                    >
                                        Back to Login
                                    </button>
                                </div>
                                <br /><br /><br /><br />
                                <Box mt={5}>
                                    <Copyright />
                                </Box>
                            </div>
                        )}
                    </div>
                </Grid>
            </Grid>
            <SupportBot />
        </>
    );
}
